<template>
  <div class="whatsapp-support-widget font-sans text-foreground antialiased" aria-label="Contact support">
    <ClAnimatePresence>
      <ClMotion v-if="state === 'open'" preset="scaleIn" class="widget-popup">
        <div class="widget-popup-inner">
          <div class="widget-popup-header">
            <div class="widget-logo-circle">
              <img src="/assets/images/clinch-logo-widget.png" alt="CLINCH" class="widget-logo" />
            </div>
            <span class="widget-greeting-short" v-if="isAuthenticated && userName">Hi {{ userName }}!</span>
            <button type="button" class="widget-close" aria-label="Close" @click="closePanel">×</button>
          </div>

          <div class="widget-content">
            <!-- Home -->
            <div v-if="activeTab === 'home'" class="widget-tab-content">
              <p class="widget-how">How can we help?</p>
              <template v-if="isAuthenticated">
                <button type="button" class="widget-cta-btn" @click="goToMessages">
                  <span class="widget-fab-icon small">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z"/></svg></span>
                  Chat with us
                </button>
                <button type="button" class="widget-cta-btn secondary" @click="goToCreateTicket">
                  <span class="widget-fab-icon small"><svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 14H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg></span>
                  Create a ticket
                </button>
              </template>
              <template v-else>
                <p class="widget-sign-in">Sign in to create tickets or chat.</p>
                <a :href="whatsappUrl" target="_blank" rel="noopener noreferrer" class="widget-cta-btn">
                  <span class="widget-wa-icon"><svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg></span>
                  Chat on WhatsApp
                </a>
              </template>
              <p class="widget-powered">Powered by Clinch</p>
            </div>

            <!-- Tickets -->
            <div v-if="activeTab === 'tickets'" class="widget-tab-content">
              <template v-if="showCreateForm">
                <div class="widget-form-header">
                  <button type="button" class="widget-back" @click="showCreateForm = false">← Back</button>
                </div>
                <form class="widget-form" @submit.prevent="submitTicket">
                  <template v-if="!isAuthenticated">
                    <label class="widget-label">Email</label>
                    <input v-model="form.email" type="email" class="widget-input" required placeholder="your@email.com" />
                  </template>
                  <label class="widget-label">Title</label>
                  <input v-model="form.title" type="text" class="widget-input" required placeholder="Brief title" />
                  <label class="widget-label">Description</label>
                  <textarea v-model="form.description" class="widget-input widget-textarea" rows="3" required placeholder="Describe your issue"></textarea>
                  <label class="widget-label">Business (optional)</label>
                  <div class="widget-combobox">
                    <input
                      v-model="businessSearch"
                      type="text"
                      class="widget-input"
                      :placeholder="selectedBusinessName || (isAuthenticated ? (usePlatformBusinessCatalog ? 'Search businesses' : 'Your businesses') : 'Search or None')"
                      @focus="loadBusinessesOnFocus()"
                      @input="loadBusinesses"
                    />
                    <ul v-if="businessOptions.length > 0" class="widget-dropdown">
                      <li><button type="button" @click="selectBusiness(null)">None / Not applicable</button></li>
                      <li v-for="b in businessOptions" :key="b.id"><button type="button" @click="selectBusiness(b)">{{ b.business_name || b.name || `Business #${b.id}` }}</button></li>
                    </ul>
                    <span v-if="selectedBusinessName" class="widget-selected">{{ selectedBusinessName }}</span>
                  </div>
                  <label class="widget-label">Issue type</label>
                  <select v-model="form.issue_type" class="widget-input">
                    <option value="">Select</option>
                    <option value="billing">Billing</option>
                    <option value="technical">Technical</option>
                    <option value="other">Other</option>
                  </select>
                  <label class="widget-label">Reference (optional)</label>
                  <input v-model="form.reference_number" type="text" class="widget-input" placeholder="Order or ref #" />
                  <label class="widget-label">Attach images (optional, max 10)</label>
                  <div class="widget-file-wrap">
                    <input
                      ref="fileInputRef"
                      type="file"
                      class="widget-file-input-hidden"
                      accept="image/*"
                      multiple
                      @change="onAttachmentChange"
                    />
                    <button type="button" class="widget-file-trigger" @click="$refs.fileInputRef?.click()">
                      {{ formAttachmentFiles.length ? `${formAttachmentFiles.length} image(s) selected` : 'Choose images' }}
                    </button>
                    <button v-if="formAttachmentFiles.length" type="button" class="widget-file-clear" @click="clearAttachments" aria-label="Clear selection">×</button>
                  </div>
                  <p v-if="ticketError" class="widget-error">{{ ticketError }}</p>
                  <p v-if="ticketSuccess" class="widget-success">{{ ticketSuccess }}</p>
                  <button type="submit" class="widget-cta-btn widget-submit-btn" :disabled="ticketSubmitting">Submit</button>
                </form>
              </template>
              <template v-else-if="selectedTicketId">
                <!-- Ticket detail: show comments from support -->
                <div class="widget-form-header">
                  <button type="button" class="widget-back" @click="closeTicketDetail">← Back to list</button>
                </div>
                <div v-if="ticketDetailLoading" class="widget-loading">Loading…</div>
                <template v-else-if="selectedTicket">
                  <div class="widget-ticket-detail-header">
                    <span class="widget-ticket-detail-title">{{ selectedTicket.title }}</span>
                    <span class="widget-ticket-detail-meta">{{ selectedTicket.status }} · {{ formatDate(selectedTicket.created_at) }}</span>
                  </div>
                  <p v-if="selectedTicket.description" class="widget-ticket-detail-desc">{{ selectedTicket.description }}</p>
                  <div v-if="selectedTicket.comments && selectedTicket.comments.length" class="widget-ticket-comments">
                    <h4 class="widget-ticket-comments-title">Updates from support</h4>
                    <div v-for="c in selectedTicket.comments" :key="c.id" class="widget-ticket-comment">
                      <p class="widget-ticket-comment-body">{{ c.body }}</p>
                      <span class="widget-ticket-comment-meta">{{ c.author_display }} · {{ formatTime(c.created_at) }}</span>
                    </div>
                  </div>
                  <p v-else class="widget-empty">No updates yet. Support will add a comment when they update your ticket.</p>
                </template>
              </template>
              <template v-else>
                <div class="widget-tickets-header">
                  <h3 class="widget-tab-title">My tickets</h3>
                  <button type="button" class="widget-link-btn" @click="openCreateForm">Create ticket</button>
                </div>
                <div v-if="ticketsLoading" class="widget-loading">Loading…</div>
                <div v-if="!ticketsLoading && tickets.length === 0" class="widget-empty">No tickets yet. Create one below.</div>
                <ul v-else class="widget-ticket-list">
                  <li v-for="t in tickets" :key="t.id" class="widget-ticket-item" @click="openTicket(t)">
                    <span class="widget-ticket-title">{{ t.title }}</span>
                    <span class="widget-ticket-meta">{{ t.status }} · {{ formatDate(t.created_at) }}</span>
                  </li>
                </ul>
              </template>
            </div>

            <!-- Messages -->
            <div v-if="activeTab === 'messages'" class="widget-tab-content widget-messages">
              <!-- Coming soon: hide real chat, show placeholder + redirect to tickets -->
              <template v-if="messagesTabComingSoon">
                <div class="widget-coming-soon">
                  <p class="widget-coming-soon-title">Coming soon</p>
                  <p class="widget-coming-soon-text">In-app chat is on its way. Use tickets to get help in the meantime.</p>
                  <button type="button" class="widget-cta-btn widget-submit-btn" @click="goToTicketsFromMessages">
                    Go to Tickets
                  </button>
                </div>
              </template>
              <template v-else>
                <div class="widget-messages-list" ref="messagesList">
                  <div v-if="messagesLoading && messages.length === 0" class="widget-loading">Loading…</div>
                  <div v-else-if="messages.length === 0" class="widget-empty">Start the conversation. Send a message below.</div>
                  <div v-else v-for="m in messages" :key="m.id" :class="['widget-msg', m.role === 'user' ? 'widget-msg-user' : 'widget-msg-support']">
                    <span class="widget-msg-body">{{ m.body }}</span>
                    <span class="widget-msg-time">{{ formatTime(m.created_at) }}</span>
                  </div>
                </div>
                <form class="widget-messages-form" @submit.prevent="sendMessage">
                  <input v-model="messageBody" type="text" class="widget-input" placeholder="Type a message…" :disabled="!isAuthenticated" />
                  <button type="submit" class="widget-send-btn" :disabled="!messageBody.trim() || messageSending">Send</button>
                </form>
              </template>
            </div>

            <!-- Help -->
            <div v-if="activeTab === 'help'" class="widget-tab-content">
              <h3 class="widget-tab-title">Help</h3>
              <template v-if="isAuthenticated">
                <HelpSearch placeholder="Search the guides" @navigate="state = 'cta'" />
                <p class="widget-help-text mt-2">
                  <router-link to="/help" @click="state = 'cta'">Browse all guides</router-link>
                </p>
              </template>
              <p v-else class="widget-help-text">
                <router-link to="/login" @click="state = 'cta'">Sign in</router-link> to search the guides for your role.
              </p>
              <p class="widget-help-links">
                <button type="button" class="widget-link-button" @click="switchToMessages">Contact support</button>
              </p>
            </div>
          </div>

          <nav class="widget-bottom-nav">
            <button type="button" :class="['widget-nav-item', activeTab === 'home' && 'active']" @click="activeTab = 'home'">
              <span class="widget-nav-icon">⌂</span><span>Home</span>
            </button>
            <button type="button" :class="['widget-nav-item', activeTab === 'tickets' && 'active']" @click="activeTab = 'tickets'; showCreateForm = false; closeTicketDetail()">
              <span class="widget-nav-icon">🎫</span><span>Tickets</span>
            </button>
            <button type="button" :class="['widget-nav-item', activeTab === 'messages' && 'active']" @click="switchToMessages">
              <span class="widget-nav-icon">💬</span><span>Messages</span>
            </button>
            <button type="button" :class="['widget-nav-item', activeTab === 'help' && 'active']" @click="activeTab = 'help'">
              <span class="widget-nav-icon">?</span><span>Help</span>
            </button>
          </nav>
        </div>
      </ClMotion>
    </ClAnimatePresence>

    <div class="widget-stack">
      <ClAnimatePresence>
        <ClMotion v-if="state === 'cta'" preset="fade" class="widget-cta-bubble">
          <span class="widget-cta-text">Chat with Us</span>
          <button type="button" class="widget-cta-close" aria-label="Dismiss" @click.stop="state = 'collapsed'">×</button>
          <span class="widget-cta-tail" aria-hidden="true"></span>
        </ClMotion>
      </ClAnimatePresence>

      <button
        type="button"
        class="widget-fab"
        :aria-label="state === 'open' ? 'Close chat' : 'Open support'"
        @click="toggleMain"
      >
        <span v-if="supportUnreadCount > 0" class="widget-fab-badge">{{ supportUnreadCount > 99 ? '99+' : supportUnreadCount }}</span>
        <span v-if="state === 'open'" class="widget-fab-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><path d="M7 10l5 5 5-5z"/></svg>
        </span>
        <span v-else class="widget-fab-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z"/>
          </svg>
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import api from '@/utils/api'
import ClAnimatePresence from '@/components/ds/ClAnimatePresence.vue'
import ClMotion from '@/components/ds/ClMotion.vue'
import HelpSearch from '@/components/help/HelpSearch.vue'
import { shouldUsePlatformBusinessCatalog } from '@/composables/usePermissions'

const POLL_INTERVAL_MS = 5000
const BUSINESS_DEBOUNCE_MS = 300

export default {
  name: 'WhatsAppSupportWidget',
  components: { ClAnimatePresence, ClMotion, HelpSearch },
  data() {
    return {
      state: 'cta',
      activeTab: 'home',
      showCreateForm: false,
      messagesTabComingSoon: true,
      selectedTicketId: null,
      selectedTicket: null,
      ticketDetailLoading: false,
      tickets: [],
      ticketsLoading: false,
      messages: [],
      messagesLoading: false,
      messageBody: '',
      messageSending: false,
      supportUnreadCount: 0,
      pollTimer: null,
      businessSearch: '',
      businessOptions: [],
      businessOptionsFull: [],
      businessSearchTimer: null,
      form: {
        email: '',
        title: '',
        description: '',
        business: null,
        issue_type: '',
        reference_number: ''
      },
      selectedBusinessName: '',
      formAttachmentFiles: [],
      ticketError: '',
      ticketSuccess: '',
      ticketSubmitting: false
    }
  },
  computed: {
    ...mapGetters(['isAuthenticated', 'currentUser', 'supportWhatsappUrl']),
    usePlatformBusinessCatalog() {
      return shouldUsePlatformBusinessCatalog(this.currentUser)
    },
    userName() {
      const u = this.currentUser
      if (!u) return ''
      return u.name || (u.email && u.email.split('@')[0]) || 'there'
    },
    /** Prefer GET /api/auth/public-site-config/ (loaded on app init); else env fallbacks in Vuex getter. */
    whatsappUrl() {
      return this.supportWhatsappUrl || '#'
    }
  },
  watch: {
    state(n) {
      if (n === 'open' && this.isAuthenticated) this.fetchUnreadCount()
    },
    activeTab(tab) {
      if (tab === 'messages') {
        this.markRead()
        this.startMessagesPolling()
      } else {
        this.stopMessagesPolling()
      }
      if (tab === 'tickets' && !this.showCreateForm && !this.selectedTicketId) this.loadTickets()
    }
  },
  mounted() {
    if (this.isAuthenticated) this.fetchUnreadCount()
  },
  beforeUnmount() {
    this.stopMessagesPolling()
  },
  methods: {
    closePanel() {
      this.state = 'cta'
    },
    toggleMain() {
      if (this.state === 'open') this.state = 'cta'
      else if (this.state === 'collapsed') this.state = 'cta'
      else this.state = 'open'
    },
    goToMessages() {
      this.activeTab = 'messages'
      this.markRead()
      this.loadMessages()
      this.startMessagesPolling()
    },
    goToCreateTicket() {
      this.activeTab = 'tickets'
      this.showCreateForm = true
      this.closeTicketDetail()
      if (this.isAuthenticated && this.currentUser?.email) this.form.email = this.currentUser.email
      this.loadBusinessesOnFocus()
    },
    openCreateForm() {
      this.showCreateForm = true
      if (this.isAuthenticated && this.currentUser?.email) this.form.email = this.currentUser.email
      this.loadBusinessesOnFocus()
    },
    switchToMessages() {
      this.activeTab = 'messages'
      this.markRead()
      this.loadMessages()
      this.startMessagesPolling()
    },
    async fetchUnreadCount() {
      if (!this.isAuthenticated) return
      try {
        const res = await api.get('/support-chat/unread-count/', {
          headers: { 'X-Session-Key': localStorage.getItem('session_key') }
        })
        this.supportUnreadCount = res.data?.count ?? 0
      } catch {
        this.supportUnreadCount = 0
      }
    },
    async markRead() {
      if (!this.isAuthenticated) return
      try {
        await api.post('/support-chat/mark-read/', {}, {
          headers: { 'X-Session-Key': localStorage.getItem('session_key') }
        })
        this.supportUnreadCount = 0
      } catch (_) {}
    },
    async loadTickets() {
      if (!this.isAuthenticated) return
      this.ticketsLoading = true
      try {
        const res = await api.get('/support-tickets/', {
          headers: { 'X-Session-Key': localStorage.getItem('session_key') }
        })
        this.tickets = res.data || []
      } catch {
        this.tickets = []
      } finally {
        this.ticketsLoading = false
      }
    },
    async loadBusinessesOnFocus() {
      if (!this.isAuthenticated) {
        this.businessOptionsFull = []
        this.businessOptions = []
        return
      }
      const headers = { 'X-Session-Key': localStorage.getItem('session_key') }
      try {
        if (this.usePlatformBusinessCatalog) {
          const res = await api.get('/admin/businesses/', {
            params: { page_size: 500 },
            headers
          })
          const list = Array.isArray(res.data) ? res.data : (res.data?.results || [])
          this.businessOptionsFull = list
        } else {
          const res = await api.get('/support-tickets/selectable-businesses/', { headers })
          const list = Array.isArray(res.data) ? res.data : []
          this.businessOptionsFull = list
        }
        this.applyBusinessFilter()
      } catch {
        this.businessOptionsFull = []
        this.businessOptions = []
      }
    },
    applyBusinessFilter() {
      const q = (this.businessSearch || '').trim().toLowerCase()
      if (!q) {
        this.businessOptions = [...this.businessOptionsFull]
        return
      }
      this.businessOptions = this.businessOptionsFull.filter(
        b => (b.business_name || b.name || '').toLowerCase().includes(q)
      )
    },
    loadBusinesses() {
      if (!this.isAuthenticated) {
        this.businessOptions = []
        return
      }
      clearTimeout(this.businessSearchTimer)
      this.businessSearchTimer = setTimeout(async () => {
        if (!this.businessOptionsFull.length) {
          await this.loadBusinessesOnFocus()
        }
        this.applyBusinessFilter()
      }, BUSINESS_DEBOUNCE_MS)
    },
    selectBusiness(b) {
      this.form.business = b ? b.id : null
      this.selectedBusinessName = b ? b.business_name : 'None'
      this.businessSearch = ''
      this.businessOptions = []
    },
    onAttachmentChange(e) {
      const files = Array.from(e.target.files || [])
      const max = 10
      this.formAttachmentFiles = files.slice(0, max)
      if (files.length > max) this.ticketError = `Only the first ${max} images will be uploaded.`
      else this.ticketError = ''
      e.target.value = ''
    },
    clearAttachments() {
      this.formAttachmentFiles = []
      this.ticketError = ''
      const input = this.$refs.fileInputRef
      if (input) input.value = ''
    },
    openTicket(t) {
      if (!t || !t.id) return
      this.selectedTicketId = t.id
      this.selectedTicket = null
      this.ticketDetailLoading = true
      api.get(`/support-tickets/${t.id}/`, {
        headers: { 'X-Session-Key': localStorage.getItem('session_key') }
      })
        .then(res => {
          this.selectedTicket = res.data
        })
        .catch(() => {
          this.selectedTicketId = null
        })
        .finally(() => {
          this.ticketDetailLoading = false
        })
    },
    closeTicketDetail() {
      this.selectedTicketId = null
      this.selectedTicket = null
    },
    goToTicketsFromMessages() {
      this.activeTab = 'tickets'
      this.showCreateForm = false
      this.closeTicketDetail()
    },
    async submitTicket() {
      this.ticketError = ''
      this.ticketSuccess = ''
      this.ticketSubmitting = true
      const email = this.isAuthenticated ? (this.currentUser?.email || this.form.email) : this.form.email
      try {
        const hasFiles = this.formAttachmentFiles.length > 0
        if (hasFiles) {
          const formData = new FormData()
          formData.append('title', this.form.title)
          formData.append('description', this.form.description)
          formData.append('email', email)
          if (this.form.business != null && this.form.business !== '') formData.append('business', this.form.business)
          if (this.form.issue_type) formData.append('issue_type', this.form.issue_type)
          if (this.form.reference_number) formData.append('reference_number', this.form.reference_number)
          this.formAttachmentFiles.forEach(f => formData.append('attachments', f))
          await api.post('/support-tickets/', formData, {
            headers: { 'X-Session-Key': localStorage.getItem('session_key') }
          })
        } else {
          await api.post('/support-tickets/', {
            title: this.form.title,
            description: this.form.description,
            email,
            business: this.form.business,
            issue_type: this.form.issue_type || undefined,
            reference_number: this.form.reference_number || undefined
          }, {
            headers: { 'X-Session-Key': localStorage.getItem('session_key') }
          })
        }
        this.ticketSuccess = 'Ticket created.'
        this.form = { email: this.isAuthenticated ? (this.currentUser?.email || '') : this.form.email, title: '', description: '', business: null, issue_type: '', reference_number: '' }
        this.selectedBusinessName = ''
        this.clearAttachments()
        this.loadTickets()
        setTimeout(() => { this.showCreateForm = false; this.closeTicketDetail(); this.ticketSuccess = '' }, 1500)
      } catch (e) {
        this.ticketError = e.response?.data?.attachments || e.response?.data?.detail || e.response?.data?.title?.[0] || 'Failed to create ticket.'
      } finally {
        this.ticketSubmitting = false
      }
    },
    async loadMessages() {
      if (!this.isAuthenticated) return
      this.messagesLoading = true
      try {
        const res = await api.get('/support-chat/messages/', {
          headers: { 'X-Session-Key': localStorage.getItem('session_key') }
        })
        this.messages = res.data || []
      } catch {
        this.messages = []
      } finally {
        this.messagesLoading = false
      }
      this.$nextTick(() => this.scrollMessagesToBottom())
    },
    startMessagesPolling() {
      this.stopMessagesPolling()
      if (!this.isAuthenticated) return
      this.pollTimer = setInterval(() => {
        if (this.activeTab === 'messages') this.loadMessages()
      }, POLL_INTERVAL_MS)
    },
    stopMessagesPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    async sendMessage() {
      const body = (this.messageBody || '').trim()
      if (!body || !this.isAuthenticated || this.messageSending) return
      this.messageSending = true
      try {
        const res = await api.post('/support-chat/messages/', { body }, {
          headers: { 'X-Session-Key': localStorage.getItem('session_key') }
        })
        this.messages.push(res.data)
        this.messageBody = ''
        this.$nextTick(() => this.scrollMessagesToBottom())
      } catch (_) {}
      finally {
        this.messageSending = false
      }
    },
    scrollMessagesToBottom() {
      const el = this.$refs.messagesList
      if (el) el.scrollTop = el.scrollHeight
    },
    formatDate(s) {
      if (!s) return ''
      const d = new Date(s)
      return d.toLocaleDateString()
    },
    formatTime(s) {
      if (!s) return ''
      const d = new Date(s)
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  }
}
</script>

<style scoped>
.whatsapp-support-widget {
  /* Tuck into the true bottom-right corner; small size avoids covering form actions */
  position: fixed;
  bottom: max(8px, env(safe-area-inset-bottom, 0px));
  right: max(8px, env(safe-area-inset-right, 0px));
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  pointer-events: none;
  transform: scale(0.88);
  transform-origin: bottom right;
}
.whatsapp-support-widget > * { pointer-events: auto; }

.widget-stack {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.widget-popup {
  position: absolute;
  bottom: 52px;
  right: 0;
  width: 300px;
  max-width: calc(100vw - 24px);
  max-height: 85vh;
}
.widget-popup-inner {
  background: linear-gradient(165deg, #1a202c 0%, #2d3748 45%, #1e293b 100%);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 32px rgba(0,0,0,0.4);
  display: flex;
  flex-direction: column;
  max-height: 75vh;
}
.widget-popup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
}
.widget-logo-circle {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}
.widget-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
}
.widget-greeting-short { font-size: 0.9rem; color: rgba(255, 255, 255, 0.95); }
.widget-close {
  width: 32px; height: 32px;
  border: none; background: rgba(255, 255, 255, 0.12); border-radius: 50%;
  font-size: 1.25rem; line-height: 1; cursor: pointer; color: rgba(255, 255, 255, 0.9);
}
.widget-close:hover { background: rgba(255, 255, 255, 0.2); }

.widget-content {
  flex: 1;
  min-height: 280px;
  overflow-y: auto;
  padding: 12px;
  color: rgba(255, 255, 255, 0.9);
}
.widget-tab-content { padding: 0 4px; }
.widget-how { font-weight: 600; margin: 0 0 12px; font-size: 1rem; color: rgba(255, 255, 255, 0.95); }
.widget-cta-btn {
  display: flex; align-items: center; gap: 8px;
  width: 100%; padding: 10px 14px; margin-bottom: 8px;
  background: rgba(255, 255, 255, 0.15); color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2); border-radius: 8px; font-weight: 600; font-size: 0.9rem;
  cursor: pointer; text-align: left;
}
.widget-cta-btn:hover { background: rgba(255, 255, 255, 0.22); }
.widget-cta-btn.secondary { background: rgba(37, 211, 102, 0.25); border-color: rgba(37, 211, 102, 0.4); }
.widget-cta-btn.secondary:hover { background: rgba(37, 211, 102, 0.35); }
.widget-cta-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.widget-submit-btn {
  background: var(--whatsapp-green, #25d366);
  border-color: rgba(255, 255, 255, 0.25);
  margin-top: 8px;
}
.widget-submit-btn:hover:not(:disabled) {
  background: #2ee56e;
  border-color: rgba(255, 255, 255, 0.3);
}
.widget-fab-icon.small { display: inline-flex; }
.widget-sign-in { font-size: 0.85rem; color: rgba(255, 255, 255, 0.8); margin: 0 0 10px; }
.widget-powered { font-size: 0.75rem; color: rgba(255, 255, 255, 0.5); margin-top: 12px; text-align: center; }

.widget-tickets-header, .widget-form-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.widget-tab-title { margin: 0; font-size: 1rem; color: rgba(255, 255, 255, 0.95); }
.widget-link-btn, .widget-back { background: none; border: none; color: #7dd3fc; cursor: pointer; font-size: 0.9rem; }
.widget-link-btn:hover, .widget-back:hover { text-decoration: underline; }
.widget-loading, .widget-empty { font-size: 0.9rem; color: rgba(255, 255, 255, 0.7); padding: 12px 0; }
.widget-ticket-list { list-style: none; padding: 0; margin: 0; }
.widget-ticket-item {
  padding: 10px 0; border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex; flex-direction: column; gap: 2px;
}
.widget-ticket-item { cursor: pointer; }
.widget-ticket-item:hover { background: rgba(255, 255, 255, 0.06); border-radius: 8px; }
.widget-ticket-title { font-weight: 500; color: rgba(255, 255, 255, 0.95); }
.widget-ticket-meta { font-size: 0.8rem; color: rgba(255, 255, 255, 0.6); }

.widget-ticket-detail-header { margin-bottom: 8px; }
.widget-ticket-detail-title { font-weight: 600; font-size: 1rem; color: rgba(255, 255, 255, 0.95); display: block; }
.widget-ticket-detail-meta { font-size: 0.8rem; color: rgba(255, 255, 255, 0.6); }
.widget-ticket-detail-desc { font-size: 0.85rem; color: rgba(255, 255, 255, 0.8); margin: 0 0 12px; padding-bottom: 12px; border-bottom: 1px solid rgba(255, 255, 255, 0.1); line-height: 1.4; }
.widget-ticket-comments-title { font-size: 0.85rem; font-weight: 600; color: rgba(255, 255, 255, 0.9); margin: 0 0 8px; }
.widget-ticket-comments { margin-top: 8px; }
.widget-ticket-comment { padding: 10px 0; border-bottom: 1px solid rgba(255, 255, 255, 0.08); }
.widget-ticket-comment:last-child { border-bottom: none; }
.widget-ticket-comment-body { font-size: 0.9rem; color: rgba(255, 255, 255, 0.95); margin: 0 0 4px; line-height: 1.4; }
.widget-ticket-comment-meta { font-size: 0.75rem; color: rgba(255, 255, 255, 0.5); }

.widget-form .widget-label { display: block; font-size: 0.8rem; margin: 8px 0 4px; color: rgba(255, 255, 255, 0.8); }
/* Force light bg + dark text so inputs are readable in all browsers/themes (avoid white-on-white) */
.widget-input, .widget-textarea {
  width: 100%; padding: 8px 10px; border: 1px solid rgba(0, 0, 0, 0.2); border-radius: 6px;
  box-sizing: border-box; font-size: 0.9rem;
  background: #fff;
  color: #1a1a1a;
  -webkit-appearance: none;
  appearance: none;
}
.widget-input::placeholder, .widget-textarea::placeholder { color: #6b7280; }
.widget-input[type="email"]:focus, .widget-input[type="text"]:focus, .widget-textarea:focus, select.widget-input:focus {
  outline: none; border-color: var(--whatsapp-green, #25d366); box-shadow: 0 0 0 2px rgba(37, 211, 102, 0.25);
}
select.widget-input {
  cursor: pointer;
  background-color: #fff;
  color: #1a1a1a;
}
select.widget-input option {
  background: #fff;
  color: #1a1a1a;
}
.widget-textarea { resize: vertical; min-height: 60px; }
.widget-combobox { position: relative; }
.widget-dropdown {
  position: absolute; top: 100%; left: 0; right: 0;
  list-style: none; padding: 0; margin: 0; background: #2d3748;
  border: 1px solid rgba(255, 255, 255, 0.15); border-radius: 6px; max-height: 160px; overflow-y: auto; z-index: 10;
}
.widget-dropdown li button {
  width: 100%; padding: 8px 10px; border: none; background: none; text-align: left; cursor: pointer; font-size: 0.9rem; color: #fff;
}
.widget-dropdown li button:hover { background: rgba(255, 255, 255, 0.1); }
.widget-selected { font-size: 0.85rem; color: rgba(255, 255, 255, 0.8); }

.widget-file-wrap { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.widget-file-input-hidden { position: absolute; width: 0; height: 0; opacity: 0; pointer-events: none; }
.widget-file-trigger {
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.95);
  font-size: 0.9rem;
  cursor: pointer;
}
.widget-file-trigger:hover { background: rgba(255, 255, 255, 0.18); }
.widget-file-clear {
  width: 28px; height: 28px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.widget-file-clear:hover { background: rgba(255, 255, 255, 0.25); }

.widget-error { color: #fca5a5; font-size: 0.85rem; margin-top: 6px; }
.widget-success { color: #86efac; font-size: 0.85rem; margin-top: 6px; }

.widget-messages { display: flex; flex-direction: column; height: 100%; min-height: 260px; }
.widget-coming-soon {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 24px 16px; min-height: 220px; text-align: center;
}
.widget-coming-soon-title { font-size: 1.1rem; font-weight: 700; color: rgba(255, 255, 255, 0.95); margin: 0 0 8px; }
.widget-coming-soon-text { font-size: 0.9rem; color: rgba(255, 255, 255, 0.7); margin: 0 0 20px; line-height: 1.4; }
.widget-messages-list {
  flex: 1; overflow-y: auto; padding: 8px 0; min-height: 180px;
}
.widget-msg { max-width: 85%; padding: 8px 10px; border-radius: 10px; margin-bottom: 6px; }
.widget-msg-user { align-self: flex-end; background: var(--whatsapp-green, #25d366); color: #fff; }
.widget-msg-support { align-self: flex-start; background: rgba(255, 255, 255, 0.15); color: #fff; }
.widget-msg-time { font-size: 0.7rem; opacity: 0.8; display: block; margin-top: 2px; }
.widget-messages-form { display: flex; gap: 8px; padding-top: 8px; flex-shrink: 0; }
.widget-messages-form .widget-input { flex: 1; }
.widget-send-btn {
  padding: 8px 14px; background: var(--whatsapp-green, #25d366); color: #fff;
  border: none; border-radius: 8px; font-weight: 600; cursor: pointer;
}
.widget-send-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.widget-help-text { font-size: 0.9rem; color: rgba(255, 255, 255, 0.7); margin: 0 0 8px; }
.widget-help-links a, .widget-help-links .widget-link-button { color: #7dd3fc; font-size: 0.9rem; }
.widget-link-button { background: none; border: none; cursor: pointer; padding: 0; text-decoration: underline; }
.widget-link-button:hover { color: #bae6fd; }

.widget-bottom-nav {
  display: flex; justify-content: space-around; padding: 8px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1); background: rgba(0, 0, 0, 0.2); border-radius: 0 0 14px 14px; flex-shrink: 0;
}
.widget-nav-item {
  display: flex; flex-direction: column; align-items: center; gap: 2px;
  background: none; border: none; font-size: 0.7rem; color: rgba(255, 255, 255, 0.6); cursor: pointer; padding: 4px 8px;
}
.widget-nav-item:hover { color: rgba(255, 255, 255, 0.9); }
.widget-nav-item.active { color: var(--whatsapp-green, #25d366); font-weight: 600; }
.widget-nav-icon { font-size: 1.1rem; }

.widget-fab {
  width: 44px; height: 44px; border-radius: 50%;
  background: var(--whatsapp-green, #25d366); color: #fff; border: none;
  cursor: pointer; box-shadow: 0 3px 10px rgba(37,211,102,0.4);
  display: flex; align-items: center; justify-content: center; position: relative;
  flex-shrink: 0;
}
.widget-fab:hover { transform: scale(1.05); }
.widget-fab-icon { display: block; line-height: 0; }
.widget-fab-icon svg { width: 22px; height: 22px; }
.widget-fab-badge {
  position: absolute;
  top: -3px;
  right: -3px;
  min-width: 16px;
  height: 16px;
  background: linear-gradient(135deg, #ff6b35 0%, #e55a2b 100%);
  color: white;
  border-radius: 50%;
  font-size: 0.65rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  border: 2px solid white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}

/* Sit beside the FAB; tail + bubble share one continuous green outline */
.widget-cta-bubble {
  position: absolute;
  right: calc(100% + 8px);
  bottom: 14px;
  top: auto;
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: none;
  border-radius: 22px;
  padding: 6px 8px 6px 12px;
  margin: 0;
  max-width: 148px;
  white-space: nowrap;
  overflow: visible;
  /* drop-shadow traces the combined bubble+tail silhouette (no border gaps) */
  filter:
    drop-shadow(0 0 0.65px var(--whatsapp-green, #25d366))
    drop-shadow(0 0 0.65px var(--whatsapp-green, #25d366))
    drop-shadow(0 2px 6px rgba(0, 0, 0, 0.12));
  box-shadow: none;
}

.widget-cta-bubble::before,
.widget-cta-bubble::after {
  content: none;
  display: none;
}

/* Solid diamond overlaps the bubble so outline is continuous */
.widget-cta-tail {
  position: absolute;
  right: -5px;
  top: 50%;
  width: 10px;
  height: 10px;
  margin-top: -5px;
  background: #fff;
  transform: rotate(-45deg);
  pointer-events: none;
  z-index: 0;
}

.widget-cta-text,
.widget-cta-close {
  position: relative;
  z-index: 1;
}
.widget-cta-text { font-weight: 600; font-size: 0.8rem; color: #333; white-space: nowrap; }
.widget-cta-close {
  width: 20px; height: 20px; border-radius: 50%; border: none;
  background: #f0f0f0; color: #333; font-size: 1rem; line-height: 1; cursor: pointer;
  flex-shrink: 0;
}

</style>
